import { useCallback, useEffect, useState } from 'react'
import { getAssuranceState, hasVerifiedTotpFactor } from '@/auth/mfa'

/**
 * Reactive MFA status for the current session: whether a verified TOTP factor
 * exists and whether the session is already stepped up to aal2.
 *
 * Drives the StaffMfaGate (block while no verified factor) and the Security
 * page status block. `refresh` lets callers re-read after enrolling/removing a
 * factor without a full remount.
 */
export interface MfaStatus {
  loading: boolean
  hasVerifiedFactor: boolean
  /** Session AAL right now (`aal1` until a step-up/enrolment verify lands). */
  currentLevel: string | null
  refresh: () => void
}

export function useMfaStatus(): MfaStatus {
  const [loading, setLoading] = useState(true)
  const [hasVerifiedFactor, setHasVerifiedFactor] = useState(false)
  const [currentLevel, setCurrentLevel] = useState<string | null>(null)
  const [tick, setTick] = useState(0)

  // Bump the tick and flip back to loading from the event handler (not the
  // effect body) so eslint's react-hooks/set-state-in-effect stays happy.
  const refresh = useCallback(() => {
    setLoading(true)
    setTick((value) => value + 1)
  }, [])

  useEffect(() => {
    let cancelled = false
    void Promise.all([hasVerifiedTotpFactor(), getAssuranceState()]).then(
      ([verified, assurance]) => {
        if (cancelled) return
        setHasVerifiedFactor(verified)
        setCurrentLevel(assurance.currentLevel)
        setLoading(false)
      },
    )
    return () => {
      cancelled = true
    }
  }, [tick])

  return { loading, hasVerifiedFactor, currentLevel, refresh }
}
